import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Stepper, Step, StepLabel, Button, Box, Typography,
  CircularProgress, Alert, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Paper, Select,
  MenuItem, FormControl, Chip, Stack, Divider, Tooltip,
  IconButton,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import * as XLSX from 'xlsx';
import { useGetJobSkills, useImportCandidatesFromCSV, useBulkUploadProfiles } from '../../hooks/useCandidateManagement';
import { mobileClean } from '../../utils/mobileClean';

const STEPS = ['Upload File', 'Map Columns', 'Preview & Submit'];
const MAX_CANDIDATES = 200;

const SYSTEM_FIELDS = [
  { key: 'candidate_name', label: 'Candidate Name', required: true, resumeOnly: false },
  { key: 'email', label: 'Email', required: true, resumeOnly: false },
  { key: 'mobile', label: 'Mobile Number', required: true, resumeOnly: false },
  { key: 'candidate_current_location', label: 'Location / City', required: true, resumeOnly: false },
  { key: 'resume_filename', label: 'Resume File Name', required: true, resumeOnly: true },
  { key: 'gender', label: 'Gender', required: false, resumeOnly: false },
  { key: 'currency', label: 'Currency', required: false, resumeOnly: false },
  { key: 'budget', label: 'Budget / Salary', required: false, resumeOnly: false },
  { key: 'budget_eom', label: 'Budget Period (UOM)', required: false, resumeOnly: false },
  { key: 'important_note', label: 'Note / Remark', required: false, resumeOnly: false },
];

const AUTO_MAP = {
  candidate_name: ['name', 'candidate', 'fullname', 'full name', 'candidate name'],
  email: ['email', 'mail', 'e-mail', 'email id'],
  mobile: ['mobile', 'phone', 'contact', 'number', 'tel', 'mobile no', 'phone no'],
  candidate_current_location: ['location', 'city', 'place', 'current location', 'current city'],
  resume_filename: ['resume', 'cv', 'file', 'filename', 'resume file', 'cv file', 'resume_filename', 'resume filename'],
  gender: ['gender', 'sex', 'gender identity'],
  currency: ['currency', 'curr'],
  budget: ['budget', 'salary', 'ctc', 'pay', 'rate', 'compensation'],
  budget_eom: ['uom', 'period', 'frequency', 'per', 'budget uom', 'payment period'],
  important_note: ['note', 'notes', 'remark', 'remarks', 'comment', 'comments'],
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const isValidMobile = (mobile) => /^\+91\d{10}$/.test(mobile);

const BulkCsvImportDialog = ({ open, onClose, jobCode, pipelineType }) => {
  const { data: jobSkills, isLoading: skillsLoading } = useGetJobSkills(open ? jobCode : null);
  const { mutateAsync: importCandidates } = useImportCandidatesFromCSV();
  const { mutateAsync: bulkUpload } = useBulkUploadProfiles();

  const isResumeJob = pipelineType === 'resume_based'
    || (!pipelineType && jobSkills?.pipeline_type === 'resume_based');

  const [step, setStep] = useState(0);
  const [csvFileName, setCsvFileName] = useState('');
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [resumeFiles, setResumeFiles] = useState({});
  const [mapping, setMapping] = useState({});
  const [processedRows, setProcessedRows] = useState([]);
  const [fileParseError, setFileParseError] = useState(null);
  const [resumeFileError, setResumeFileError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const validRows = processedRows.filter(r => r._valid);
  const invalidRows = processedRows.filter(r => !r._valid);

  const resetState = useCallback(() => {
    setStep(0);
    setCsvFileName('');
    setCsvHeaders([]);
    setRawRows([]);
    setResumeFiles({});
    setMapping({});
    setProcessedRows([]);
    setFileParseError(null);
    setResumeFileError(null);
    setSubmitting(false);
    setSubmitResult(null);
    setSubmitError(null);
  }, []);

  const handleClose = useCallback((event, reason) => {
    if (reason === 'backdropClick') return;
    resetState();
    onClose();
  }, [resetState, onClose]);

  // Auto-map column headers to system fields
  useEffect(() => {
    if (!csvHeaders.length) return;
    const m = {};
    SYSTEM_FIELDS.forEach(({ key }) => {
      const keywords = AUTO_MAP[key] || [];
      const match = csvHeaders.find(h =>
        keywords.some(kw => h.toLowerCase().includes(kw))
      );
      m[key] = match || '';
    });
    setMapping(m);
  }, [csvHeaders]);

  const ALLOWED_CSV_EXTS = ['.csv', '.xlsx', '.xls'];

  const handleCsvFile = useCallback(async (file) => {
    if (!file) return;
    setFileParseError(null);

    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_CSV_EXTS.includes(ext)) {
      setFileParseError(`Invalid file type "${file.name}". Please upload a CSV or Excel file (.csv, .xlsx, .xls).`);
      return;
    }

    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

      if (json.length < 2) {
        setFileParseError('File has no data rows. Please check your file.');
        return;
      }

      const headers = json[0].map(h => String(h).trim()).filter(h => h);
      const rows = json.slice(1)
        .filter(row => row.some(cell => String(cell).trim() !== ''))
        .map(row => {
          const obj = {};
          headers.forEach((h, i) => {
            obj[h] = row[i] !== undefined ? String(row[i]).trim() : '';
          });
          return obj;
        });

      if (rows.length > MAX_CANDIDATES) {
        setFileParseError(`File contains ${rows.length} rows. Maximum allowed is ${MAX_CANDIDATES} candidates per upload. Please split into smaller files.`);
        return;
      }

      setCsvFileName(file.name);
      setCsvHeaders(headers);
      setRawRows(rows);
    } catch {
      setFileParseError('Failed to parse file. Please use a valid Excel (.xlsx/.xls) or CSV file.');
    }
  }, []);

  const ALLOWED_RESUME_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  const ALLOWED_RESUME_EXTS = ['.pdf', '.doc', '.docx'];

  const handleResumeFiles = useCallback((files) => {
    const invalid = Array.from(files).filter(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      return !ALLOWED_RESUME_TYPES.includes(f.type) && !ALLOWED_RESUME_EXTS.includes(ext);
    });

    if (invalid.length > 0) {
      setResumeFileError(
        `Invalid file type${invalid.length > 1 ? 's' : ''}: ${invalid.map(f => f.name).join(', ')}. Only PDF, DOC, and DOCX files are allowed.`
      );
      setResumeFiles({});
      return;
    }

    setResumeFileError(null);
    const fileMap = {};
    Array.from(files).forEach(f => {
      fileMap[f.name.toLowerCase()] = f;
    });
    setResumeFiles(fileMap);
  }, []);

  const activeFields = SYSTEM_FIELDS.filter(f => !f.resumeOnly || isResumeJob);

  const requiredFieldsMapped = activeFields
    .filter(f => f.required)
    .every(f => mapping[f.key]);

  const step0NextEnabled = csvHeaders.length > 0
    && (!isResumeJob || (Object.keys(resumeFiles).length > 0 && !resumeFileError));

  const processRows = useCallback(() => {
    const processed = rawRows.map((row, idx) => {
      const candidate = {};
      const errors = [];

      // Apply mapping
      SYSTEM_FIELDS.forEach(({ key }) => {
        const headerKey = mapping[key];
        candidate[key] = headerKey ? (row[headerKey] || '') : '';
      });

      // Required field checks
      if (!candidate.candidate_name) errors.push('Name missing');
      if (!candidate.email) errors.push('Email missing');
      if (!candidate.mobile) errors.push('Mobile missing');
      if (!candidate.candidate_current_location) errors.push('Location missing');

      // Email format
      if (candidate.email && !isValidEmail(candidate.email)) {
        errors.push('Invalid email');
      }

      // Mobile clean + validate
      if (candidate.mobile) {
        const cleaned = mobileClean(candidate.mobile);
        if (!isValidMobile(cleaned)) {
          errors.push('Invalid mobile');
        } else {
          candidate.mobile = cleaned;
        }
      }

      // Budget must be non-negative if provided
      if (candidate.budget !== '' && candidate.budget !== undefined) {
        const budgetNum = Number(candidate.budget);
        if (isNaN(budgetNum) || budgetNum < 0) {
          errors.push('Budget must be a positive number');
        }
      }

      // Resume matching for resume jobs
      let resumeFound = false;
      if (isResumeJob) {
        if (!candidate.resume_filename) {
          errors.push('Resume filename missing');
        } else {
          resumeFound = !!resumeFiles[candidate.resume_filename.toLowerCase()];
          if (!resumeFound) errors.push('Resume file not uploaded');
        }
      }

      return {
        ...candidate,
        _rowIndex: idx,
        _errors: errors,
        _resumeFound: resumeFound,
        _valid: errors.length === 0,
      };
    });

    setProcessedRows(processed);
  }, [rawRows, mapping, resumeFiles, isResumeJob]);

  const handleNext = useCallback(() => {
    if (step === 1) processRows();
    setStep(s => s + 1);
  }, [step, processRows]);

  const handleBack = useCallback(() => {
    setStep(s => s - 1);
  }, []);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const stripMeta = ({ _rowIndex, _errors, _resumeFound, _valid, ...c }) => c;

      if (!isResumeJob) {
        const result = await importCandidates({
          job_code: jobCode,
          candidates: validRows.map(stripMeta),
        });
        setSubmitResult(result);
      } else {
        const coreSkillId = jobSkills?.coreSkills?.id;
        const cleanRows = validRows.map(({ resume_filename, ...rest }) => stripMeta({ ...rest }));
        const enriched = cleanRows.map(c => ({ ...c, coreSkillId }));

        const formData = new FormData();
        formData.append('job_code', jobCode);
        formData.append('candidates', JSON.stringify(enriched));

        // Files MUST be appended in same order as candidates
        validRows.forEach(row => {
          const file = resumeFiles[row.resume_filename?.toLowerCase()];
          if (file) formData.append('resumes', file);
        });

        const result = await bulkUpload({ formData });
        setSubmitResult(result);
      }
    } catch (err) {
      if (err.response?.status === 413) {
        setSubmitError(
          'Upload payload is too large. Please split your file into smaller batches (recommended: up to 200 candidates per upload).'
        );
      } else {
        setSubmitError(
          err.response?.data?.message || err.message || 'Submission failed. Please try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  }, [isResumeJob, jobCode, validRows, resumeFiles, jobSkills, importCandidates, bulkUpload]);

  // ── Render steps ──────────────────────────────────────────────────────────

  const renderStep0 = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* CSV / Excel upload */}
      <Box>
        <Typography variant="subtitle2" mb={1}>
          Step 1: Upload your candidate file (Excel or CSV)
        </Typography>
        <Button
          variant="outlined"
          component="label"
          startIcon={<UploadFileIcon />}
        >
          Choose File (.xlsx / .xls / .csv)
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            hidden
            onChange={e => handleCsvFile(e.target.files[0])}
          />
        </Button>
        {csvFileName && (
          <Box mt={1}>
            <Chip
              label={`${csvFileName} — ${rawRows.length} rows detected`}
              color="success"
              size="small"
              onDelete={() => {
                setCsvFileName('');
                setCsvHeaders([]);
                setRawRows([]);
                setFileParseError(null);
              }}
            />
          </Box>
        )}
        {fileParseError && (
          <Alert severity="error" sx={{ mt: 1 }}>{fileParseError}</Alert>
        )}
      </Box>

      {/* Resume files upload — resume jobs only */}
      {isResumeJob && (
        <>
          <Divider />
          <Box>
            <Typography variant="subtitle2" mb={0.5}>
              Step 2: Upload all resume files (PDF / DOC / DOCX)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Each candidate's "Resume File Name" in the Excel must exactly match one of the uploaded filenames.
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
            >
              Choose Resume Files (multi-select)
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                multiple
                hidden
                onChange={e => handleResumeFiles(e.target.files)}
              />
            </Button>
            {resumeFileError && (
              <Alert severity="error" sx={{ mt: 1 }}>{resumeFileError}</Alert>
            )}
            {Object.keys(resumeFiles).length > 0 && (
              <Box mt={1} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, maxHeight: 130, overflowY: 'auto' }}>
                {Object.keys(resumeFiles).map(name => (
                  <Chip
                    key={name}
                    label={name}
                    size="small"
                    color="primary"
                    variant="outlined"
                    onDelete={() => {
                      const updated = { ...resumeFiles };
                      delete updated[name];
                      setResumeFiles(updated);
                      if (Object.keys(updated).length === 0) setResumeFileError(null);
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );

  const renderStep1 = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Match your file's columns to the system fields. Required fields are marked with *.
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>System Field</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Your Column</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeFields.map(field => (
              <TableRow key={field.key}>
                <TableCell>
                  {field.label}
                  {field.required && <span style={{ color: 'red' }}> *</span>}
                </TableCell>
                <TableCell>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={mapping[field.key] || ''}
                      onChange={e => setMapping(m => ({ ...m, [field.key]: e.target.value }))}
                      displayEmpty
                    >
                      <MenuItem value=""><em>Not Mapped</em></MenuItem>
                      {csvHeaders.map(h => (
                        <MenuItem key={h} value={h}>{h}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!requiredFieldsMapped && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Please map all required fields (*) before proceeding.
        </Alert>
      )}
    </Box>
  );

  const renderStep2 = () => {
    if (submitResult) {
      const allFailed = submitResult.created === 0;
      const partial = submitResult.errors > 0 && submitResult.created > 0;
      return (
        <Box>
          <Alert severity={allFailed ? 'error' : partial ? 'warning' : 'success'} sx={{ mb: 2 }}>
            {submitResult.created} candidate(s) created successfully.
            {submitResult.errors > 0 && ` ${submitResult.errors} failed.`}
          </Alert>
          {submitResult.error_details?.length > 0 && (
            <Box>
              <Typography variant="subtitle2" mb={1}>Failed entries:</Typography>
              {submitResult.error_details.map((e, i) => (
                <Typography key={i} variant="caption" display="block" color="error">
                  • {e.email}: {e.error}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      );
    }

    const displayRows = [...processedRows].sort((a, b) => a._rowIndex - b._rowIndex);

    return (
      <Box>
        <Stack direction="row" spacing={1} mb={2} alignItems="center">
          <Chip label={`${validRows.length} valid`} color="success" size="small" />
          <Chip label={`${invalidRows.length} invalid`} color="error" size="small" />
          {isResumeJob && (
            <Chip
              label={`${validRows.filter(r => r._resumeFound).length} resumes matched`}
              color="primary"
              size="small"
            />
          )}
        </Stack>

        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>
        )}

        <TableContainer component={Paper} sx={{ maxHeight: 380 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Location</TableCell>
                {isResumeJob && <TableCell>Resume</TableCell>}
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.map((row) => (
                <TableRow
                  key={row._rowIndex}
                  sx={{ backgroundColor: row._valid ? '#e8f5e9' : '#ffebee' }}
                >
                  <TableCell>{row._rowIndex + 1}</TableCell>
                  <TableCell>{row.candidate_name || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.email || '—'}
                  </TableCell>
                  <TableCell>{row.mobile || '—'}</TableCell>
                  <TableCell>{row.candidate_current_location || '—'}</TableCell>
                  {isResumeJob && (
                    <TableCell>
                      {row._resumeFound
                        ? <CheckCircleIcon fontSize="small" color="success" />
                        : <CancelIcon fontSize="small" color="error" />}
                    </TableCell>
                  )}
                  <TableCell>
                    {row._valid ? (
                      <Typography variant="caption" color="success.main">✓ Valid</Typography>
                    ) : (
                      <Tooltip title={row._errors.join(', ')}>
                        <Typography variant="caption" color="error">
                          {row._errors[0]}{row._errors.length > 1 ? ` +${row._errors.length - 1}` : ''}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderContent = () => {
    if (skillsLoading && step === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    if (step === 0) return renderStep0();
    if (step === 1) return renderStep1();
    return renderStep2();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Bulk Upload Candidates</Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderContent()}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Stack direction="row" spacing={1}>
          {step > 0 && !submitResult && (
            <Button onClick={handleBack} disabled={submitting}>
              Back
            </Button>
          )}
          {step < 2 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                (step === 0 && !step0NextEnabled) ||
                (step === 1 && !requiredFieldsMapped)
              }
            >
              Next
            </Button>
          )}
          {step === 2 && !submitResult && (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={validRows.length === 0 || submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {submitting
                ? 'Submitting...'
                : `Submit ${validRows.length} Candidate${validRows.length !== 1 ? 's' : ''}`}
            </Button>
          )}
          {submitResult && (
            <Button variant="contained" onClick={handleClose}>
              Done
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default BulkCsvImportDialog;
